import {
  type AssemblyMateControllerViewModel,
} from '@puppycad/cad-core';
import type { PropertyManagerContext } from './types';
import { resolveAssemblyMateDiagnosticText } from './assembly-mate-diagnostic';

type SolveErrorFeatureKind = 'fillet' | 'chamfer' | 'extrude' | 'revolve' | 'revolve-cut' | 'assembly-mate';

interface ActiveSolveError {
  featureKind: SolveErrorFeatureKind;
  rawError: string;
}

interface AssemblyMateControllerReadable {
  /** 读取 cad-core 配合控制器快照。 */
  getViewModel?: () => AssemblyMateControllerViewModel;
  /** 控制器是否处于 Mate 命令会话。 */
  isActive?: () => boolean;
}

interface AssemblyMateRendererReadable {
  /** cad-core 标准配合控制器。 */
  assemblyMateController?: AssemblyMateControllerReadable;
}

/**
 * 读取字符串形式的求解错误。
 *
 * @param value 待检查的错误值。
 * @returns 可展示前先进入文案归一化流程的原始错误。
 */
function readStringError(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return null;
  }
  return value;
}

/**
 * 读取拉伸状态里的预览错误。
 *
 * 设计说明：旧版拉伸上下文同时暴露 `extrudePreviewError` 和
 * `extrudeState.previewError`，这里统一只做读取，不让视图组件关心状态来源。
 *
 * @param context 属性面板上下文。
 * @returns 拉伸预览错误。
 */
function readExtrudePreviewError(context: PropertyManagerContext): string | null {
  const directError = readStringError(context.extrudePreviewError);
  if (directError) {
    return directError;
  }

  const extrudeState = context.extrudeState;
  if (!extrudeState || typeof extrudeState !== 'object') {
    return null;
  }

  return readStringError((extrudeState as { previewError?: unknown }).previewError);
}

/**
 * 从状态对象中读取预览错误。
 *
 * 设计说明：旋转凸台和旋转切除都通过状态快照暴露 `previewError`，
 * 这里抽取通用读取逻辑，避免不同特征各自散落类型判断。
 *
 * @param state 特征属性面板状态。
 * @returns 预览错误文案。
 */
function readStatePreviewError(state: unknown): string | null {
  if (!state || typeof state !== 'object') {
    return null;
  }
  return readStringError((state as { previewError?: unknown }).previewError);
}

/**
 * 读取标准配合控制器中的当前诊断。
 *
 * 设计说明：
 * 1. Mate 的验证、预览与求解都在 cad-core 内完成，PropertyManager 只读取控制器快照；
 * 2. 这里不解析 BRep、不判断配合合法性，只把 cad-core 输出转为统一顶部错误提示；
 * 3. 命令未激活时不展示，避免设计树 hover Mate 时污染普通属性面板。
 *
 * @param context 属性面板上下文。
 * @returns 当前 Mate 诊断文案。
 */
function readAssemblyMateSolveError(context: PropertyManagerContext): string | null {
  const runtime = context.renderer as AssemblyMateRendererReadable | null | undefined;
  const controller = runtime?.assemblyMateController;
  if (!controller) {
    return null;
  }
  const viewModel = controller.getViewModel?.();
  if (!viewModel?.state) {
    return null;
  }
  const isMateSessionActive = context.activeCommand === 'assembly-mate'
    || viewModel.state.active === true
    || controller.isActive?.() === true;
  if (!isMateSessionActive) {
    return null;
  }
  return resolveAssemblyMateDiagnosticText(viewModel.state);
}

/**
 * 读取旋转凸台/基体状态里的预览错误。
 *
 * @param context 属性面板上下文。
 * @returns 旋转凸台/基体预览错误。
 */
function readRevolvePreviewError(context: PropertyManagerContext): string | null {
  const directError = readStringError((context as { revolvePreviewError?: unknown }).revolvePreviewError);
  if (directError) {
    return directError;
  }
  return readStatePreviewError(context.revolveState);
}

/**
 * 读取旋转切除状态里的预览错误。
 *
 * @param context 属性面板上下文。
 * @returns 旋转切除预览错误。
 */
function readRevolveCutPreviewError(context: PropertyManagerContext): string | null {
  const directError = readStringError((context as { revolveCutPreviewError?: unknown }).revolveCutPreviewError);
  if (directError) {
    return directError;
  }
  return readStatePreviewError(context.revolveCutState);
}

/**
 * 判断拉伸属性面板是否处于激活状态。
 *
 * @param context 属性面板上下文。
 * @returns 拉伸属性面板是否激活。
 */
function isExtrudeActiveContext(context: PropertyManagerContext): boolean {
  if (context.extrudeActive === true) {
    return true;
  }

  const extrudeState = context.extrudeState;
  if (!extrudeState || typeof extrudeState !== 'object') {
    return false;
  }

  return (extrudeState as { active?: unknown }).active === true;
}

/**
 * 判断旋转凸台/基体属性面板是否处于激活状态。
 *
 * @param context 属性面板上下文。
 * @returns 旋转凸台/基体属性面板是否激活。
 */
function isRevolveActiveContext(context: PropertyManagerContext): boolean {
  if (context.revolveActive === true) {
    return true;
  }
  const revolveState = context.revolveState;
  if (!revolveState || typeof revolveState !== 'object') {
    return false;
  }
  return (revolveState as { active?: unknown }).active === true;
}

/**
 * 判断旋转切除属性面板是否处于激活状态。
 *
 * @param context 属性面板上下文。
 * @returns 旋转切除属性面板是否激活。
 */
function isRevolveCutActiveContext(context: PropertyManagerContext): boolean {
  if (context.revolveCutActive === true) {
    return true;
  }
  const revolveCutState = context.revolveCutState;
  if (!revolveCutState || typeof revolveCutState !== 'object') {
    return false;
  }
  return (revolveCutState as { active?: unknown }).active === true;
}

/**
 * 读取当前激活特征的原始求解错误。
 *
 * @param context 属性面板上下文。
 * @returns 当前激活特征的原始求解错误。
 */
function resolveActiveSolveError(context: PropertyManagerContext): ActiveSolveError | null {
  const filletError = readStringError(context.filletPreviewError);
  if (context.filletActive === true && filletError) {
    return {
      featureKind: 'fillet',
      rawError: filletError,
    };
  }

  const chamferError = readStringError(context.chamferPreviewError);
  if (context.chamferActive === true && chamferError) {
    return {
      featureKind: 'chamfer',
      rawError: chamferError,
    };
  }

  const extrudeError = readExtrudePreviewError(context);
  if (isExtrudeActiveContext(context) && extrudeError) {
    return {
      featureKind: 'extrude',
      rawError: extrudeError,
    };
  }

  const revolveCutError = readRevolveCutPreviewError(context);
  if (isRevolveCutActiveContext(context) && revolveCutError) {
    return {
      featureKind: 'revolve-cut',
      rawError: revolveCutError,
    };
  }

  const revolveError = readRevolvePreviewError(context);
  if (isRevolveActiveContext(context) && revolveError) {
    return {
      featureKind: 'revolve',
      rawError: revolveError,
    };
  }

  const assemblyMateError = readAssemblyMateSolveError(context);
  if (assemblyMateError) {
    return {
      featureKind: 'assembly-mate',
      rawError: assemblyMateError,
    };
  }

  return null;
}

/**
 * 判断当前上下文是否为切除拉伸。
 *
 * @param context 属性面板上下文。
 * @returns 是否为切除拉伸上下文。
 */
function isCutExtrudeContext(context: PropertyManagerContext): boolean {
  if (context.extrudeOperation === 'cut') {
    return true;
  }

  const extrudeState = context.extrudeState;
  if (!extrudeState || typeof extrudeState !== 'object') {
    return false;
  }

  const state = extrudeState as {
    operation?: unknown;
    definition?: {
      featureKind?: unknown;
    };
  };

  return state.operation === 'cut' || state.definition?.featureKind === 'cut-extrude';
}

/**
 * 判断当前上下文是否正在处理部分边线圆角/倒角。
 *
 * @param context 属性面板上下文。
 * @param rawError 原始错误消息。
 * @returns 是否是部分边线求解失败。
 */
function isPartialEdgeBlendSolveError(
  context: PropertyManagerContext,
  rawError: string,
): boolean {
  if (/部分边线|partial[- ]?edge/i.test(rawError)) {
    return true;
  }
  if (context.filletActive === true) {
    return context.filletDefinition?.partialEdgeParametersEnabled === true;
  }
  if (context.chamferActive === true) {
    return context.chamferDefinition?.partialEdgeParametersEnabled === true;
  }
  return false;
}

/**
 * 判断旋转错误是否已经是用户可读的校验提示。
 *
 * 设计说明：
 * - 旋转功能在 composable 层会产出“请选择旋转轴”等明确操作提示；
 * - 这些文案应该原样进入顶部 popover，而不是被归一化为泛化求解失败；
 * - 但工程异常仍需要走兜底文案，避免把 Puppy CAD 内核/JS 异常细节暴露给用户。
 *
 * @param rawError 原始错误消息。
 * @returns 是否可以直接展示。
 */
function isRevolveValidationMessage(rawError: string): boolean {
  if (/structuredClone|TypeError|ReferenceError|Puppy CAD 内核|Puppy CAD 内核|undefined|null|failed/i.test(rawError)) {
    return false;
  }
  return /请选择|角度|方向|轮廓|旋转轴/.test(rawError);
}

/**
 * 解析旋转类特征的展示文案。
 *
 * @param rawError 原始错误消息。
 * @param fallbackMessage 泛化兜底文案。
 * @returns 顶部 popover 展示文案。
 */
function resolveRevolveSolveErrorMessage(rawError: string, fallbackMessage: string): string {
  if (isRevolveValidationMessage(rawError)) {
    return rawError;
  }
  return fallbackMessage;
}

/**
 * 解析属性面板可展示的求解错误文案。
 *
 * 设计说明：
 * 1. Worker 和几何层错误包含 Puppy CAD 内核、type 等工程上下文，不应该直接暴露给用户；
 * 2. PropertyManager 只展示操作级失败原因，贴近 SolidWorks 的属性栏提示；
 * 3. 该函数只负责文案归一化，不改变任何求解、提交或预览状态。
 *
 * @param context 属性面板上下文。
 * @returns 用户可理解的错误文案；没有错误时返回 `null`。
 */
export function resolvePropertyManagerSolveErrorMessage(
  context: PropertyManagerContext,
): string | null {
  const activeError = resolveActiveSolveError(context);
  if (!activeError) {
    return null;
  }

  if (isPartialEdgeBlendSolveError(context, activeError.rawError)) {
    return '无法创建部分边线圆角/倒角。';
  }

  if (activeError.featureKind === 'fillet') {
    return '无法创建圆角。请检查所选项目和参数。';
  }

  if (activeError.featureKind === 'chamfer') {
    return '无法创建倒角。请检查所选项目和参数。';
  }

  if (activeError.featureKind === 'extrude') {
    if (isCutExtrudeContext(context)) {
      return '无法创建拉伸切除。请检查所选轮廓和参数。';
    }
    return '无法创建拉伸。请检查所选轮廓和参数。';
  }

  if (activeError.featureKind === 'revolve-cut') {
    return resolveRevolveSolveErrorMessage(
      activeError.rawError,
      '无法创建旋转切除。请检查旋转轴、所选轮廓和参数。',
    );
  }

  if (activeError.featureKind === 'revolve') {
    return resolveRevolveSolveErrorMessage(
      activeError.rawError,
      '无法创建旋转凸台/基体。请检查旋转轴、所选轮廓和参数。',
    );
  }

  if (activeError.featureKind === 'assembly-mate') {
    return activeError.rawError;
  }

  return '无法创建特征。请检查所选项目和参数。';
}
